import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { 
  Plus, Search, Download, LogOut, User, AlertTriangle,
  Filter, X, Package, ShieldAlert, ShoppingCart, Trash2, Shield, FileText, FileSpreadsheet, Phone, ChevronRight, RefreshCw, ClipboardList, Loader2, Mail, FileX, PartyPopper
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import NotificationBell from "@/components/NotificationBell";
import PushNotificationPrompt from "@/components/PushNotificationPrompt";
import TheftHeatmap from "@/components/TheftHeatmap";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  Active: "status-active",
  Stolen: "status-stolen",
  Sold: "status-sold",
  Discarded: "status-discarded",
  "Written Off": "status-discarded"
};

const STATUS_ICONS = {
  Active: Package,
  Stolen: ShieldAlert,
  Sold: ShoppingCart,
  Discarded: Trash2,
  "Written Off": FileX
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, stolen: 0, sold: 0, discarded: 0, written_off: 0, recovered: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilterState] = useState(searchParams.get("status") || "");
  const [manufacturerFilter, setManufacturerFilterState] = useState(searchParams.get("manufacturer") || "");

  const setStatusFilter = (val) => {
    setStatusFilterState(val);
    const params = new URLSearchParams(searchParams);
    if (val) params.set("status", val); else params.delete("status");
    setSearchParams(params, { replace: true });
  };
  const setManufacturerFilter = (val) => {
    setManufacturerFilterState(val);
    const params = new URLSearchParams(searchParams);
    if (val) params.set("manufacturer", val); else params.delete("manufacturer");
    setSearchParams(params, { replace: true });
  };
  const [showFilters, setShowFilters] = useState(!!(searchParams.get("status") || searchParams.get("manufacturer")));
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Insurance report states
  const [showInsuranceDialog, setShowInsuranceDialog] = useState(false);
  const [insuranceStep, setInsuranceStep] = useState(1); // 1=info, 2=select tools, 3=crime ref
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [crimeReference, setCrimeReference] = useState("");
  const [generatingReport, setGeneratingReport] = useState(false);

  const checkAdmin = async () => {
    try {
      const response = await fetch(`${API}/admin/check`, { 
        headers: getAuthHeaders()
      });
      setIsAdmin(response.ok);
    } catch {
      setIsAdmin(false);
    }
  };

  const fetchTools = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter) params.append("status", statusFilter);
      if (manufacturerFilter) params.append("manufacturer", manufacturerFilter);

      const response = await fetch(`${API}/tools?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setTools(data);
      }
    } catch (error) {
      console.error("Error fetching tools:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/stats`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Haptic feedback helper - with iOS fallback using AudioContext
  const triggerHaptic = useCallback((pattern = [50]) => {
    // Try standard Vibration API first (Android, some browsers)
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
        return;
      } catch (e) {
        // Vibration failed, try fallback
      }
    }
    
    // iOS fallback: Create a subtle audio "tick" using AudioContext
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const audioCtx = new AudioContextClass();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        oscillator.frequency.value = 150;
        oscillator.type = 'sine';
        
        const duration = Math.min(pattern[0] || 50, 30) / 1000;
        gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + duration / 2);
        gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + duration);
        
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + duration);
        
        setTimeout(() => audioCtx.close(), 100);
      }
    } catch (e) {
      // Audio feedback not available, fail silently
    }
  }, []);

  // Pull to refresh handler with haptic feedback
  const handleRefresh = async () => {
    // Trigger haptic feedback on pull start
    triggerHaptic([30]); // Short vibration on pull
    
    setIsRefreshing(true);
    await Promise.all([fetchTools(), fetchStats()]);
    setIsRefreshing(false);
    
    // Trigger success haptic feedback
    triggerHaptic([20, 50, 20]); // Double tap pattern for success
    toast.success("Inventory refreshed!");
    return Promise.resolve();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTools(), fetchStats(), checkAdmin()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Check if user needs to add phone number
  useEffect(() => {
    if (user && !user.phone) {
      // Check if user has dismissed this prompt before in this session
      const dismissed = sessionStorage.getItem('phonePromptDismissed');
      if (!dismissed) {
        setShowPhonePrompt(true);
      }
    }
  }, [user]);

  const dismissPhonePrompt = () => {
    setShowPhonePrompt(false);
    sessionStorage.setItem('phonePromptDismissed', 'true');
  };

  useEffect(() => {
    fetchTools();
  }, [searchQuery, statusFilter, manufacturerFilter]);

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API}/tools/export/csv`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tools_export_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("CSV exported!");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await fetch(`${API}/tools/export/pdf`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tools_report_${new Date().toISOString().split("T")[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("PDF report downloaded!");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setManufacturerFilter("");
  };

  // Insurance Report Functions
  const stolenToolsForReport = tools.filter(t => t.status === "Stolen");
  
  const openInsuranceDialog = () => {
    if (stolenToolsForReport.length === 0) {
      toast.error("You don't have any tools marked as stolen");
      return;
    }
    setInsuranceStep(1);
    setSelectedToolIds([]);
    setCrimeReference("");
    setShowInsuranceDialog(true);
  };
  
  const toggleToolSelection = (toolId) => {
    setSelectedToolIds(prev => 
      prev.includes(toolId) 
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };
  
  const selectAllStolenTools = () => {
    setSelectedToolIds(stolenToolsForReport.map(t => t.tool_id));
  };
  
  const generateInsuranceReport = async () => {
    if (selectedToolIds.length === 0) {
      toast.error("Please select at least one tool");
      return;
    }
    if (!crimeReference.trim() || crimeReference.trim().length < 3) {
      toast.error("Please enter a valid Crime Reference Number");
      return;
    }
    
    setGeneratingReport(true);
    try {
      const response = await fetch(`${API}/tools/insurance-report`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          tool_ids: selectedToolIds,
          crime_reference: crimeReference.trim()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to generate report");
      }
      
      const data = await response.json();
      toast.success(`Report sent to your email (${user.email})`);
      setShowInsuranceDialog(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const stolenTools = tools.filter(t => t.status === "Stolen");
  const otherTools = tools.filter(t => t.status !== "Stolen");

  // Filtered stats - computed from the tools array which is already filtered by backend
  const hasFilter = statusFilter || manufacturerFilter;
  const filteredStats = {
    total: tools.length,
    active: tools.filter(t => t.status === "Active").length,
    stolen: tools.filter(t => t.status === "Stolen").length,
    recovered: tools.filter(t => t.status === "Recovered").length,
    sold: tools.filter(t => t.status === "Sold").length,
    total_value: tools.reduce((sum, t) => sum + (t.scan_price || t.estimated_value || 0), 0)
  };
  const displayStats = hasFilter ? filteredStats : stats;
  const valueLabel = hasFilter ? "Filtered Value" : "Inventory Value";

  // Get unique manufacturers for filter
  const manufacturers = [...new Set(tools.map(t => t.manufacturer))].sort();

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="min-h-screen bg-background">
        {/* Desktop Header - Hidden on Mobile */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40 hidden md:block">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/news" className="text-sm text-orange-400 hover:text-orange-300 transition-colors">
                News
              </Link>
              <Link to="/insurance" className="text-sm text-green-400 hover:text-green-300 transition-colors">
                Insurance
              </Link>
              <Link to="/contact" className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors">
                Contact
              </Link>
            </nav>
          </div>
          
          <div className="flex items-center gap-2 lg:gap-4">
            {isAdmin && (
              <Link to="/admin">
                <Button variant="outline" size="sm" className="border-primary text-primary" data-testid="admin-panel-btn">
                  <Shield className="w-4 h-4 mr-1 lg:mr-2" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/add-tool">
              <Button className="btn-primary" size="sm" data-testid="add-tool-btn">
                <Plus className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Add Tool</span>
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-secondary"
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4 mr-1 lg:mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background border-border">
                <DropdownMenuItem onClick={handleExportCSV} className="cursor-pointer" data-testid="export-csv-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPDF} className="cursor-pointer" data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
                {stolenToolsForReport.length > 0 && (
                  <DropdownMenuItem onClick={openInsuranceDialog} className="cursor-pointer text-red-400" data-testid="insurance-report-btn">
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Insurance Claim Report
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <NotificationBell />
            <Link to="/profile">
              <Button variant="ghost" size="sm" data-testid="profile-btn">
                <User className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">{user?.name?.split(" ")[0]}</span>
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Header - Simplified */}
      <header className="mobile-sticky-header md:hidden px-4 py-3">
        <div className="flex items-center justify-between">
          <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-primary" data-testid="mobile-admin-btn">
                  <Shield className="w-5 h-5" />
                </Button>
              </Link>
            )}
            <NotificationBell />
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-export-btn">
                  <Download className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-[#0f172a] border-t border-yellow-500/20 rounded-t-2xl">
                <SheetHeader>
                  <SheetTitle className="text-white text-left">Export Inventory</SheetTitle>
                </SheetHeader>
                <div className="grid grid-cols-2 gap-4 py-6">
                  <Button 
                    onClick={handleExportCSV} 
                    className="h-20 flex flex-col gap-2 bg-white/5 hover:bg-white/10"
                    variant="ghost"
                    data-testid="mobile-export-csv"
                  >
                    <FileSpreadsheet className="w-8 h-8 text-green-400" />
                    <span className="text-white">CSV File</span>
                  </Button>
                  <Button 
                    onClick={handleExportPDF} 
                    className="h-20 flex flex-col gap-2 bg-white/5 hover:bg-white/10"
                    variant="ghost"
                    data-testid="mobile-export-pdf"
                  >
                    <FileText className="w-8 h-8 text-red-400" />
                    <span className="text-white">PDF Report</span>
                  </Button>
                  {stolenToolsForReport.length > 0 && (
                    <Button 
                      onClick={openInsuranceDialog} 
                      className="h-20 flex flex-col gap-2 bg-red-500/10 hover:bg-red-500/20 col-span-2"
                      variant="ghost"
                      data-testid="mobile-insurance-report"
                    >
                      <ClipboardList className="w-8 h-8 text-red-400" />
                      <span className="text-white">Insurance Claim Report</span>
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Mobile Welcome Section with Pull to Refresh */}
        <div className="md:hidden">
          <PullToRefresh
            onRefresh={handleRefresh}
            pullingContent={
              <div className="flex items-center justify-center py-4 text-muted-foreground">
                <RefreshCw className="w-5 h-5 mr-2" />
                <span className="text-sm">Pull to refresh...</span>
              </div>
            }
            refreshingContent={
              <div className="flex items-center justify-center py-4 text-primary">
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                <span className="text-sm">Refreshing inventory...</span>
              </div>
            }
            resistance={2.5}
            maxPullDownDistance={100}
          >
            <div className="mb-4">
              <h1 className="font-heading text-2xl font-bold">Welcome back, {user?.name?.split(" ")[0]}!</h1>
              <p className="text-base text-muted-foreground">Pull down to refresh • Your tool inventory at a glance</p>
            </div>
          </PullToRefresh>
        </div>

        {/* Desktop Welcome - no pull to refresh needed */}
        <div className="hidden md:block mb-4">
          {/* Empty for desktop - stats cards show first */}
        </div>

        {/* Stats Cards - Mobile optimized as horizontal scroll */}
        {/* Phone Number Prompt */}
        {showPhonePrompt && (
          <Alert className="mb-4 md:mb-6 border-primary/50 bg-primary/10">
            <Phone className="h-4 w-4 text-primary" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-sm md:text-base">
                <strong>Add your phone number</strong> — Police need to contact you if tools are recovered. 
                <Link to="/profile" className="text-primary hover:underline ml-1">Update profile</Link>
              </span>
              <Button variant="ghost" size="sm" onClick={dismissPhonePrompt} className="ml-2 h-6 px-2 shrink-0">
                <X className="w-4 h-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <PushNotificationPrompt />

        {/* Mobile Stats - Horizontal Scroll */}
        <div className="md:hidden flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide mb-4">
          <Card className="border-primary/30 bg-card flex-shrink-0 w-32">
            <CardContent className="p-3 text-center">
              <span className="text-2xl text-primary mx-auto mb-1 opacity-70">£</span>
              <p className="text-2xl font-heading font-bold text-primary">{displayStats.total_value ? displayStats.total_value.toLocaleString('en-GB', {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '0'}</p>
              <p className="text-base text-muted-foreground uppercase">{hasFilter ? "Filtered" : "Value"}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card flex-shrink-0 w-28">
            <CardContent className="p-3 text-center">
              <Package className="w-6 h-6 text-primary mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-heading font-bold">{displayStats.total}</p>
              <p className="text-base text-muted-foreground uppercase">Total</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card flex-shrink-0 w-28">
            <CardContent className="p-3 text-center">
              <Package className="w-6 h-6 text-green-400 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-heading font-bold text-green-400">{displayStats.active}</p>
              <p className="text-base text-muted-foreground uppercase">Active</p>
            </CardContent>
          </Card>
          <Card className="border-red-500/30 bg-card flex-shrink-0 w-28">
            <CardContent className="p-3 text-center">
              <ShieldAlert className="w-6 h-6 text-red-400 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-heading font-bold text-red-400">{displayStats.stolen}</p>
              <p className="text-base text-muted-foreground uppercase">Stolen</p>
            </CardContent>
          </Card>
          <Card className="border-green-500/30 bg-card flex-shrink-0 w-28">
            <CardContent className="p-3 text-center">
              <PartyPopper className="w-6 h-6 text-green-500 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-heading font-bold text-green-500">{displayStats.recovered}</p>
              <p className="text-base text-muted-foreground uppercase">Recovered</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card flex-shrink-0 w-28">
            <CardContent className="p-3 text-center">
              <ShoppingCart className="w-6 h-6 text-gray-400 mx-auto mb-1 opacity-70" />
              <p className="text-2xl font-heading font-bold text-gray-400">{displayStats.sold}</p>
              <p className="text-base text-muted-foreground uppercase">Sold</p>
            </CardContent>
          </Card>
        </div>

        {/* Desktop Stats Grid */}
        <div className="hidden md:grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="border-primary/30 bg-card tool-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">{valueLabel}</p>
                  <p className="text-2xl font-heading font-bold text-primary">£{displayStats.total_value ? displayStats.total_value.toLocaleString('en-GB', {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '0'}</p>
                </div>
                <span className="text-3xl text-primary opacity-50">£</span>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card tool-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Total</p>
                  <p className="text-3xl font-heading font-bold">{displayStats.total}</p>
                </div>
                <Package className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card tool-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Active</p>
                  <p className="text-3xl font-heading font-bold text-green-400">{displayStats.active}</p>
                </div>
                <Package className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card tool-card border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Stolen</p>
                  <p className="text-3xl font-heading font-bold text-red-400">{displayStats.stolen}</p>
                </div>
                <ShieldAlert className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500/30 bg-card tool-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Recovered</p>
                  <p className="text-3xl font-heading font-bold text-green-500">{displayStats.recovered}</p>
                </div>
                <PartyPopper className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card tool-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Sold</p>
                  <p className="text-3xl font-heading font-bold text-gray-400">{displayStats.sold}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-gray-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="mb-4 md:mb-6 space-y-3 md:space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card border-input h-12 md:h-10 text-base md:text-sm"
                data-testid="search-input"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className={`h-12 md:h-10 ${showFilters ? "border-primary" : "border-secondary"}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {(statusFilter || manufacturerFilter) && (
                <Badge className="ml-2 bg-primary text-primary-foreground">
                  {[statusFilter, manufacturerFilter].filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 p-4 bg-card border border-border rounded-xl md:rounded">
              <div className="flex-1">
                <label className="text-sm text-muted-foreground block mb-2">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full bg-background h-12 md:h-10" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Stolen">Stolen</SelectItem>
                    <SelectItem value="Sold">Sold</SelectItem>
                    <SelectItem value="Discarded">Discarded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm text-muted-foreground block mb-2">Manufacturer</label>
                <Select value={manufacturerFilter} onValueChange={setManufacturerFilter}>
                  <SelectTrigger className="w-full bg-background h-12 md:h-10" data-testid="manufacturer-filter">
                    <SelectValue placeholder="All Manufacturers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Manufacturers</SelectItem>
                    {manufacturers.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(statusFilter || manufacturerFilter) && (
                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="h-12 md:h-10" data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stolen Tools Section */}
        {stolenTools.length > 0 && (
          <div className="mb-6 md:mb-8">
            <Card className="stolen-section">
              <CardHeader className="pb-2 px-4 md:px-6">
                <CardTitle className="flex items-center gap-2 text-red-400 font-heading uppercase text-lg md:text-xl">
                  <AlertTriangle className="w-6 h-6" />
                  Stolen Tools ({stolenTools.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 md:px-6">
                {/* Mobile: List View */}
                {/* Mobile: Card View */}
                <div className="md:hidden flex flex-col gap-8">
                  {stolenTools.map(tool => (
                    <ToolCard key={tool.tool_id} tool={tool} />
                  ))}
                </div>
                {/* Desktop: Grid View */}
                <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {stolenTools.map(tool => (
                    <ToolCard key={tool.tool_id} tool={tool} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* All Tools */}
        <div>
          <h2 className="font-heading text-lg md:text-xl uppercase tracking-wide mb-3 md:mb-4 flex items-center gap-2 text-green-400">
            <Package className="w-6 h-6" />
            Active Tools ({otherTools.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading tools...</p>
            </div>
          ) : otherTools.length === 0 && stolenTools.length === 0 ? (
            <Card className="border-border bg-card">
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="font-heading text-xl uppercase mb-2">No Tools Yet</h3>
                <p className="text-muted-foreground mb-6">Start by adding your first power tool</p>
                <Link to="/add-tool">
                  <Button className="btn-primary h-12 md:h-10" data-testid="add-first-tool-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Your First Tool
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile: Card View */}
              <div className="md:hidden flex flex-col gap-8">
                {otherTools.map(tool => (
                  <ToolCard key={tool.tool_id} tool={tool} />
                ))}
              </div>
              {/* Desktop: Grid View */}
              <div className="hidden md:grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {otherTools.map(tool => (
                  <ToolCard key={tool.tool_id} tool={tool} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Theft Heatmap */}
        <div className="mt-8 relative z-0">
          <TheftHeatmap />
        </div>
      </main>
      <Dialog open={showInsuranceDialog} onOpenChange={setShowInsuranceDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl md:text-xl uppercase flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-red-400" />
              Insurance Claim Report
            </DialogTitle>
          </DialogHeader>

          {insuranceStep === 1 && (
            <div className="space-y-4">
              <Alert className="border-yellow-500/30 bg-yellow-500/10">
                <AlertTriangle className="w-5 h-5 md:w-4 md:h-4 text-yellow-400" />
                <AlertDescription className="text-yellow-200 text-base md:text-sm">
                  <strong className="block mb-2 text-base md:text-sm">Before proceeding:</strong>
                  Please contact your local police and obtain a <strong>Crime Reference Number</strong>. 
                  You'll need this to generate your insurance report.
                </AlertDescription>
              </Alert>
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-2 text-base md:text-sm">What this report includes:</h4>
                <ul className="text-base md:text-sm text-muted-foreground space-y-2 md:space-y-1">
                  <li>• Your contact details</li>
                  <li>• Details of each stolen tool</li>
                  <li>• Proof of registration (QR codes)</li>
                  <li>• Total estimated value</li>
                  <li>• Crime reference number</li>
                </ul>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowInsuranceDialog(false)} className="w-full sm:w-auto h-12 md:h-10 text-base md:text-sm">
                  Cancel
                </Button>
                <Button onClick={() => setInsuranceStep(2)} className="btn-primary w-full sm:w-auto h-12 md:h-10 text-base md:text-sm">
                  I Have My Crime Reference
                </Button>
              </DialogFooter>
            </div>
          )}

          {insuranceStep === 2 && (
            <div className="space-y-4">
              <DialogDescription className="text-base md:text-sm">
                Select the stolen tools to include in your insurance report:
              </DialogDescription>
              
              <div className="flex justify-between items-center">
                <span className="text-base md:text-sm text-muted-foreground">
                  {selectedToolIds.length} of {stolenToolsForReport.length} selected
                </span>
                <Button variant="link" size="sm" onClick={selectAllStolenTools} className="text-primary text-base md:text-sm">
                  Select All
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {stolenToolsForReport.map(tool => (
                  <div 
                    key={tool.tool_id}
                    className={`p-4 md:p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedToolIds.includes(tool.tool_id) 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => toggleToolSelection(tool.tool_id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        checked={selectedToolIds.includes(tool.tool_id)}
                        onCheckedChange={() => toggleToolSelection(tool.tool_id)}
                        className="w-5 h-5 md:w-4 md:h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate text-base md:text-sm">{tool.manufacturer} <span className="text-primary capitalize">{tool.tool_type}</span></p>
                        <p className="text-base md:text-sm text-muted-foreground">S/N: {tool.serial_number}</p>
                        {tool.estimated_value && (
                          <p className="text-base md:text-sm text-green-400">
                            Value: £{tool.estimated_value.toFixed(2)}
                            <span className={`ml-1.5 text-xs px-1 py-0.5 rounded ${
                              tool.price_source === 'Retail' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                            }`}>{tool.price_source === 'Retail' ? 'Retail' : 'Est'}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setInsuranceStep(1)} className="w-full sm:w-auto h-12 md:h-10 text-base md:text-sm">
                  Back
                </Button>
                <Button 
                  onClick={() => setInsuranceStep(3)} 
                  disabled={selectedToolIds.length === 0}
                  className="btn-primary w-full sm:w-auto h-12 md:h-10 text-base md:text-sm"
                >
                  Continue ({selectedToolIds.length} tools)
                </Button>
              </DialogFooter>
            </div>
          )}

          {insuranceStep === 3 && (
            <div className="space-y-4">
              <DialogDescription className="text-base md:text-sm">
                Enter your Crime Reference Number to generate the report:
              </DialogDescription>
              
              <div className="space-y-2">
                <Label htmlFor="crime-ref" className="text-base md:text-sm">Crime Reference Number</Label>
                <Input
                  id="crime-ref"
                  value={crimeReference}
                  onChange={(e) => setCrimeReference(e.target.value.toUpperCase())}
                  placeholder="e.g., 41/123456/24"
                  className="bg-background border-input h-12 md:h-10 text-base md:text-sm"
                  data-testid="crime-ref-input"
                />
                <p className="text-sm md:text-xs text-muted-foreground">
                  This is provided by the police when you report the theft
                </p>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-2 text-base md:text-sm">Report Summary:</h4>
                <p className="text-base md:text-sm text-muted-foreground">
                  Tools: {selectedToolIds.length}<br />
                  Total Value: £{stolenToolsForReport
                    .filter(t => selectedToolIds.includes(t.tool_id))
                    .reduce((sum, t) => sum + (t.estimated_value || 0), 0)
                    .toFixed(2)}
                </p>
              </div>
              
              <Alert className="border-blue-500/30 bg-blue-500/10">
                <Mail className="w-5 h-5 md:w-4 md:h-4 text-blue-400" />
                <AlertDescription className="text-blue-200 text-base md:text-sm">
                  The report will be emailed to <strong>{user?.email}</strong>
                </AlertDescription>
              </Alert>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setInsuranceStep(2)} className="w-full sm:w-auto h-12 md:h-10 text-base md:text-sm">
                  Back
                </Button>
                <Button 
                  onClick={generateInsuranceReport} 
                  disabled={generatingReport || !crimeReference.trim()}
                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto h-12 md:h-10 text-base md:text-sm"
                >
                  {generatingReport ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
                  ) : (
                    <><ClipboardList className="w-4 h-4 mr-2" /> Generate & Email Report</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
}

// Mobile Tool Item - List style for better touch interaction
function MobileToolItem({ tool }) {
  const StatusIcon = STATUS_ICONS[tool.status] || Package;
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const [imgError, setImgError] = useState(false);
  
  return (
    <Link to={`/tool/${tool.tool_id}`} data-testid={`mobile-tool-${tool.tool_id}`}>
      <div className="mobile-tool-item">
        {/* Tool Image Thumbnail */}
        <div className="w-16 h-16 rounded-lg bg-black/20 overflow-hidden flex-shrink-0">
          {tool.image_url && !imgError ? (
            <img 
              src={tool.image_url.startsWith('/api') ? API_BASE + tool.image_url : tool.image_url} 
              alt={`${tool.manufacturer} ${tool.tool_type}`}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-6 h-6 text-muted-foreground/30" />
            </div>
          )}
        </div>
        
        {/* Tool Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-heading font-bold uppercase truncate text-lg">
              {tool.manufacturer}
            </h3>
            <Badge className={`${STATUS_STYLES[tool.status]} border text-xs shrink-0`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {tool.status}
            </Badge>
          </div>
          <p className="text-base text-primary truncate capitalize">{tool.tool_type}</p>
          <p className="text-sm text-muted-foreground font-mono mt-1">S/N: {tool.serial_number}</p>
        </div>
        
        {/* Arrow indicator */}
        <ChevronRight className="w-5 h-5 text-muted-foreground/50 shrink-0" />
      </div>
    </Link>
  );
}

function ToolCard({ tool }) {
  const StatusIcon = STATUS_ICONS[tool.status] || Package;
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const [imgError, setImgError] = useState(false);
  const hasImage = tool.image_url && !imgError;
  
  return (
    <Link to={`/tool/${tool.tool_id}`} data-testid={`tool-card-${tool.tool_id}`}>
      <Card className="border-2 border-slate-600/40 bg-slate-800/50 tool-card cursor-pointer overflow-hidden hover:border-primary/50 transition-colors rounded-xl h-48 md:h-44">
        <div className="flex h-full">
          {/* Tool Image - fills full card height */}
          {hasImage ? (
            <div className="w-36 md:w-44 flex-shrink-0 overflow-hidden">
              <img 
                src={tool.image_url.startsWith('/api') ? API_BASE + tool.image_url : tool.image_url} 
                alt={`${tool.manufacturer} ${tool.tool_type}`}
                className="w-full h-full object-cover rounded-l-xl"
                onError={() => setImgError(true)}
              />
            </div>
          ) : (
            <div className="w-36 md:w-44 flex-shrink-0 bg-muted/30 flex items-center justify-center rounded-l-xl">
              <Package className="w-10 h-10 text-muted-foreground/30" />
            </div>
          )}
          {/* Right side: yellow bar + content */}
          <div className="flex-1 min-w-0 flex flex-col">
            {/* Yellow manufacturer title bar */}
            <div className="bg-[#febd17] px-3 py-0.5 rounded-tr-xl">
              <h3 className="font-heading text-lg font-bold uppercase truncate text-black tracking-wide">
                {tool.manufacturer}
              </h3>
            </div>
            {/* Content */}
            <div className="px-3 py-2 flex flex-col flex-1 overflow-hidden">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Badge className={`${STATUS_STYLES[tool.status]} border font-mono text-xs uppercase tracking-wider`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {tool.status}
                  </Badge>
                </div>
                <p className="text-primary text-base font-bold capitalize">{tool.tool_type}</p>
                {tool.model_number && (
                  <p className="text-muted-foreground text-sm mt-0.5">
                    <span className="text-foreground/70">Model:</span>{" "}
                    <span className="font-mono">{tool.model_number}</span>
                  </p>
                )}
                <p className="text-muted-foreground text-sm mt-0.5">
                  <span className="text-foreground/70">S/N:</span>{" "}
                  <span className="font-mono">{tool.serial_number}</span>
                </p>
              </div>
              <p className="text-xs text-muted-foreground font-mono truncate mt-auto pt-1.5 border-t border-border">
                ID: {tool.tool_id}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
